import { useState, useCallback, useRef } from "react";

import {
  AcceptedFileType,
  DropzoneError,
  IMAGE_FILE_TYPES,
} from "src/shared/components";
import { useGetFile } from "src/shared/hooks";
import { useUploadFile } from "src/shared/hooks/useUploadFile";
import { HackerAPIFile } from "src/shared/utils/hackerapi";

export const useFileDropzone = (
  file: HackerAPIFile | undefined,
  setFile: (files: HackerAPIFile[] | undefined) => void,
  acceptedFileTypes: AcceptedFileType[] = IMAGE_FILE_TYPES,
  validateFile?: (file: File) => boolean | Promise<boolean>,
  prepareFile?: (file: File) => File | Promise<File>
) => {
  const {
    uploadFile,
    mutationResult: { loading: isFileUploading, error: fileUploadError },
  } = useUploadFile();
  const [dropzoneErrorMessage, setDropzoneErrorMessage] = useState("");
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const dropzoneInputRef = useRef<HTMLInputElement>();
  const fetchedFile = useGetFile(file);

  const error = dropzoneErrorMessage || fileUploadError?.message;
  const loading = isFileProcessing || isFileUploading;

  const addFile = async (files: File[]) => {
    if (files.length !== 1) return;

    setIsFileProcessing(true);
    try {
      const selectedFile = files[0];
      if (validateFile && !(await validateFile(selectedFile))) {
        setDropzoneErrorMessage(
          `Please upload a valid ${acceptedFileTypes
            .map((s) => s.toUpperCase())
            .join(", ")} file.`
        );
        return;
      }

      setDropzoneErrorMessage("");
      const preparedFile = prepareFile
        ? await prepareFile(selectedFile)
        : selectedFile;
      const hapiFile = await uploadFile(preparedFile);

      if (hapiFile) setFile([hapiFile]);
    } catch (error) {
      setDropzoneErrorMessage(
        error instanceof Error
          ? error.message
          : "We couldn't prepare this file. Please try again."
      );
    } finally {
      setIsFileProcessing(false);
    }
  };

  const deleteFile = () => {
    setFile(undefined);
    if (dropzoneInputRef.current) dropzoneInputRef.current.value = "";
  };

  const onFileError = useCallback(
    (error: DropzoneError) => {
      switch (error) {
        case DropzoneError.MAX_AMOUNT:
          setDropzoneErrorMessage("Please upload a single file.");
          return;
        case DropzoneError.INVALID_FORMAT:
          setDropzoneErrorMessage(
            `Please upload a valid ${acceptedFileTypes
              .map((s) => s.toUpperCase())
              .join(", ")} file.`
          );
          return;
        default:
          return;
      }
    },
    [acceptedFileTypes]
  );

  return {
    dropzoneInputRef,
    fetchedFile,
    loading,
    error,
    addFile,
    deleteFile,
    onFileError,
    setDropzoneErrorMessage,
  };
};
